import {Button, Stack, Typography} from "@mui/material";
import {Dialog} from "../base/Dialog";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {AnnouncementsTable} from "./Announcements/AnnouncementsTable";
import {
    getNotifications,
    patchNotification,
    postNotification,
    putNotification,
} from "../../services/notificationsService";
import {NotificationStatus, NotificationType} from "../../types/domain/notification";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Campaign, Edit} from "@mui/icons-material";
import AddAnnouncement from "./AdminAnnouncements/AddAnnouncement";
import {Notification} from "../../types/domain/notification";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import dayjs from "dayjs";
import {formatToISO8601} from "../../utils/dateUtils";

export const AdminAnnouncementsView: React.FC = () => {
    const {t} = useTranslation();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [addAnnouncementDialogVisible, setAddAnnouncementDialogVisible] = useState(false);
    const [name, setName] = useState("");
    const [content, setContent] = useState("");
    const [total, setTotal] = useState(0);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedCompanyMemberRoles, setSelectedCompanyMemberRoles] = useState<CompanyMemberRole[]>([]);
    const [allCompanies, setAllCompanies] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [notificationId, setNotificationId] = useState<string | undefined>();
    const [status, setStatus] = useState(NotificationStatus.DRAFT);
    const [startTime, setStartTime] = useState<string | null>(null);

    useEffect(() => {
        void _getNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function _getNotifications() {
        const data = await getNotifications({type: NotificationType.ANNOUNCEMENT});
        setNotifications(data.data);
        setTotal(parseInt(data.total));
    }

    function resetForm() {
        setIsEditing(false);
        setNotificationId(undefined);
        setName("");
        setContent("");
        setSelectedCompanyIds([]);
        setSelectedCompanyMemberRoles([]);
        setAllCompanies(false);
        setStatus(NotificationStatus.DRAFT);
        setStartTime(null);
    }

    async function processAnnouncement() {
        if (isEditing && notificationId) {
            await putNotification(notificationId, {
                name: name,
                metaData: {
                    content,
                },
                startTime: startTime?.endsWith("Z") ? startTime : startTime + "Z",
                status,
                targetAll: allCompanies,
                targetCompanies: selectedCompanyIds,
                targetRoles: selectedCompanyMemberRoles,
            });
            setIsEditing(false);
            setNotificationId(undefined);
        } else {
            await postNotification({
                name: name,
                metaData: {
                    content,
                },
                startTime: formatToISO8601(dayjs(new Date())),
                type: NotificationType.ANNOUNCEMENT,
                status,
                targetAll: allCompanies,
                targetCompanies: selectedCompanyIds,
                targetRoles: selectedCompanyMemberRoles,
            });
        }
        setAddAnnouncementDialogVisible(false);
        resetForm();
        void _getNotifications();
    }

    function editAnnouncement(announcement: Notification) {
        if (announcement?.id) {
            setNotificationId(announcement.id);
        }
        setIsEditing(true);
        setName(announcement.name);
        setAllCompanies(!!announcement.targetAll);
        if (announcement?.metaData?.content) {
            setContent((announcement.metaData.content || "") as string);
        }
        if (announcement.targetCompanies?.length) {
            setSelectedCompanyIds(announcement.targetCompanies);
        }
        if (announcement.targetRoles?.length) {
            setSelectedCompanyMemberRoles(announcement.targetRoles);
        }
        if (announcement.startTime?.length) {
            setStartTime(announcement.startTime);
        }
        if (announcement.status?.length) {
            setStatus(announcement.status);
        }
        setAddAnnouncementDialogVisible(true);
    }

    async function changeNotificationStatus(id: string, status: NotificationStatus) {
        setNotifications(
            notifications.map((notification) => {
                if (notification.id === id) {
                    notification.status = status;
                }
                return notification;
            }),
        );
        await patchNotification(id, status);
    }

    return (
        <Stack direction="column" sx={{padding: "1.125rem"}} overflow="hidden" gap="1.5rem">
            <Stack>
                <Typography
                    fontSize={22}
                    display={"flex"}
                    alignItems="center"
                    sx={{textTransform: "uppercase"}}
                    color="primary"
                >
                    <Campaign sx={{marginRight: "12px"}} />

                    {t("announcements_page.announcements")}
                    <Button
                        sx={{whiteSpace: "nowrap", marginLeft: "auto"}}
                        startIcon={<AddIcon />}
                        onClick={() => setAddAnnouncementDialogVisible(true)}
                    >
                        {t("announcements_page.add_announcement")}
                    </Button>
                </Typography>
            </Stack>
            <Stack>
                <AnnouncementsTable
                    announcements={notifications}
                    total={total}
                    page={1}
                    rowsPerPage={10}
                    editAnnouncement={editAnnouncement}
                    setAnnouncements={setNotifications}
                    changeNotificationStatus={changeNotificationStatus}
                />
            </Stack>

            <Dialog
                title={t("announcements_page.add_announcement")}
                titleIcon={<Edit />}
                open={addAnnouncementDialogVisible}
                onClose={() => {
                    setAddAnnouncementDialogVisible(false);
                    resetForm();
                }}
                enableSubmit={true}
                onSubmit={() => void processAnnouncement()}
            >
                <AddAnnouncement
                    id={notificationId}
                    name={name}
                    content={content}
                    setName={setName}
                    setContent={setContent}
                    setSelectedCompanyIds={setSelectedCompanyIds}
                    selectedCompanyIds={selectedCompanyIds}
                    setSelectedCompanyMemberRoles={setSelectedCompanyMemberRoles}
                    selectedCompanyMemberRoles={selectedCompanyMemberRoles}
                    setAllCompanies={setAllCompanies}
                    allCompanies={allCompanies}
                    status={status}
                    setStatus={setStatus}
                    setStartTime={setStartTime}
                    startTime={startTime}
                ></AddAnnouncement>
            </Dialog>
        </Stack>
    );
};
